import React, { useState, useEffect } from 'react';

const Compiler = () => {
  const [selectedLang, setSelectedLang] = useState('Python');
  const [code, setCode] = useState('');
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);

  const languages = [
    { name: 'Python', default: 'def greet():\n    print("Hello from SENVRA AI!")\n\ngreet()' },
    { name: 'JS', default: 'console.log("Hello from SENVRA AI!");\n\n// Try simple math\nconst a = 10;\nconst b = 20;\nconsole.log("Sum:", a + b);' },
    { name: 'Java', default: 'public class Main {\n    public static void main(String[] args) {\n        System.out.println("Hello from SENVRA AI!");\n    }\n}' },
    { name: 'C++', default: '#include <iostream>\nusing namespace std;\n\nint main() {\n    cout << "Hello from SENVRA AI!" << endl;\n    return 0;\n}' },
    { name: 'C', default: '#include <stdio.h>\n\nint main() {\n    printf("Hello from SENVRA AI!\\n");\n    return 0;\n}' },
    { name: 'PHP', default: '<?php\necho "Hello from SENVRA AI!";\n?>' }
  ];

  // Update initial code when language changes
  useEffect(() => {
    const lang = languages.find(l => l.name === selectedLang);
    setCode(lang.default);
    setOutput('');
  }, [selectedLang]);

  const handleRun = () => {
    setIsRunning(true);
    setOutput('> Initializing compiler...\n> Compiling code...\n');

    setTimeout(() => {
      if (selectedLang === 'JS') {
        // Real-time JavaScript execution logic
        try {
          const originalLog = console.log;
          let logs = [];
          console.log = (...args) => {
            logs.push(args.join(' '));
          };
          // eslint-disable-next-line no-eval
          eval(code);
          console.log = originalLog;
          setOutput('> Success:\n' + logs.join('\n'));
        } catch (err) {
          setOutput('> Error:\n' + err.message);
        }
      } else {
        // Simulated execution for non-JS languages (Requires Backend API for real execution)
        setOutput(`> Output for ${selectedLang} simulated:\nHello from SENVRA AI!\nCode executed successfully.`);
      }
      setIsRunning(false);
    }, 1200);
  };

  return (
    <main className="pt-[110px] pb-10 bg-[#0f172a] min-h-screen px-4 md:px-10 text-slate-300">
      <div className="max-w-[1500px] mx-auto">
        
        {/* Top Header Toolbar - Matches image_c64f9e.png */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 bg-slate-900/80 p-3 rounded-2xl border border-slate-800 shadow-xl backdrop-blur-md">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 border-r border-slate-700 pr-6">
              <span className="text-green-500 font-black text-lg tracking-tighter">❯_ CodePlayground</span>
            </div>
            
            {/* Language Tabs */}
            <div className="flex bg-slate-800/50 p-1 rounded-xl border border-slate-700">
              {languages.map((lang) => (
                <button 
                  key={lang.name}
                  onClick={() => setSelectedLang(lang.name)}
                  className={`px-5 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedLang === lang.name ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'text-slate-400 hover:text-white'}`}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Icons Bar - Matches image_c64f9e.png */}
            <div className="hidden md:flex items-center gap-4 bg-slate-800/30 px-4 py-1.5 rounded-xl border border-slate-800 mr-2">
              <button title="Upload" className="text-slate-500 hover:text-white transition">📤</button>
              <button title="Download" className="text-slate-500 hover:text-white transition">📥</button>
              <button title="Copy" className="text-slate-500 hover:text-white transition">📋</button>
              <div className="w-[1px] h-4 bg-slate-700 mx-1"></div>
              <button title="Reset" onClick={() => setCode('')} className="text-red-500/70 hover:text-red-400 transition">🔄</button>
            </div>
            
            <button 
              onClick={handleRun}
              disabled={isRunning}
              className="bg-green-600 hover:bg-green-500 text-white px-10 py-2.5 rounded-xl font-black flex items-center gap-2 transition-all active:scale-95 shadow-lg shadow-green-900/20 disabled:opacity-50"
            >
              {isRunning ? (
                <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>▶ Run</>
              )}
            </button>
          </div>
        </div>

        {/* Professional Editor Grid - Matches image_c63ca0.png */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_450px] gap-6 h-[calc(100vh-250px)]">
          
          {/* Left Side: Code Editor */}
          <div className="bg-[#1e1e1e] rounded-[32px] border border-slate-800 overflow-hidden flex flex-col shadow-2xl relative group">
            
            {/* Editor Sub-toolbar */}
            <div className="bg-[#1a1a1a] px-8 py-3 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex gap-2 mr-4">
                  <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                </div>
                <div className="flex gap-5 text-[11px] font-black text-slate-500 uppercase tracking-widest cursor-pointer">
                  <span className="hover:text-primary transition flex items-center gap-1">📖 Explain</span>
                  <span className="hover:text-yellow-400 transition flex items-center gap-1">🔆 Debug</span>
                  <span className="hover:text-purple-400 transition flex items-center gap-1">⚡ Optimize</span>
                </div>
              </div>
              <span className="text-[10px] font-black text-slate-600 tracking-widest">{selectedLang.toUpperCase()} MODE</span>
            </div>

            <textarea 
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="flex-1 bg-transparent p-10 text-blue-300 font-mono text-base leading-relaxed outline-none resize-none spellcheck-false"
              spellCheck="false"
              placeholder={`Write your ${selectedLang} code here...`}
            />
          </div>

          {/* Right Side: Terminal Output */}
          <div className="bg-[#020617] rounded-[32px] border border-slate-800 overflow-hidden flex flex-col shadow-2xl">
            <div className="bg-slate-900/50 px-8 py-3 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5"><polyline points="4 17 10 11 4 5"></polyline><line x1="12" y1="19" x2="20" y2="19"></line></svg>
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Terminal Output</span>
              </div>
              <button 
                onClick={() => setOutput('')} 
                className="text-slate-600 hover:text-white transition"
                title="Clear Terminal"
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path></svg>
              </button>
            </div>
            
            <div className="flex-1 p-8 font-mono text-sm overflow-y-auto custom-scrollbar">
              {output ? (
                <pre className="text-emerald-400 whitespace-pre-wrap leading-loose animate-in fade-in duration-300">{output}</pre>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-800">
                   <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mb-4 border border-slate-800">
                    <span className="text-2xl text-slate-700 animate-pulse">❯_</span>
                   </div>
                   <p className="text-[10px] font-black uppercase tracking-[3px] text-slate-700">Ready to compile...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Compiler;